/**
 * How often each kind of data re-checks the server.
 *
 * ## Polling is the floor, not the transport
 *
 * **Reverb was added afterwards** — see `hooks/useRealtime.ts` — so an event
 * normally arrives over the websocket and these intervals never fire in anger.
 * This block used to argue that websockets had been rejected; that was true when
 * it was written and stopped being true the same week. Read it now as why the
 * polling stayed underneath them:
 *
 * - Reverb is a **third daemon** on the plant box, alongside `schedule:work` and
 *   `queue:work`. When a websocket server dies at 03:00 nobody notices, because
 *   the app keeps working — it just quietly stops being live, which is the worst
 *   possible failure mode for a feature whose whole promise is freshness. With
 *   polling underneath, a dead Reverb costs ten seconds of latency instead of
 *   every update.
 * - The app is **offline-first** by design. A socket drops on the far side of
 *   the plant and has to reconnect and resynchronise; a poll simply fails and
 *   the next one succeeds. TanStack Query already pauses while `navigator`
 *   reports offline and resumes on reconnect, so the offline queue and the live
 *   data share one story.
 * - For a fault queue, ten seconds is not distinguishable from instant. Nobody
 *   is racing another supervisor for a submission.
 *
 * So: do not remove these intervals because "we have websockets now". They are
 * what makes the websocket optional.
 *
 * ## What makes it feel live
 *
 * The interval matters less than `refetchOnWindowFocus`. A supervisor who
 * switches to the app from another window gets fresh data on the same frame,
 * which covers the case that actually happens: nobody watches a dashboard
 * continuously, they come back to it.
 *
 * Polling pauses automatically while the tab is hidden — TanStack's default for
 * `refetchIntervalInBackground` is `false` — so a phone in a pocket is not
 * waking its radio every ten seconds.
 */
export const LIVE = {
  /**
   * Things a supervisor is waiting on: the open queue, a submission being
   * worked, the notification bell, tasks.
   */
  operational: 10_000,

  /**
   * Aggregates. These move slowly and cost real query time, so they refresh at
   * a pace that matches how fast a chart can meaningfully change.
   */
  analytics: 60_000,

  /**
   * The audit trail. An administrator watching it wants to see actions appear,
   * but it is a review screen rather than something anyone is blocked on.
   */
  audit: 30_000,
} as const

/**
 * Options that make a query live.
 *
 * Reference data — enum labels, work centres, the user directory — deliberately
 * does not get this. It changes when an administrator edits it, not on its own,
 * and polling it every ten seconds would be pure noise on the wire.
 *
 * That reasoning is right and it was, for a while, the whole story — which is
 * how it produced a bug. Reference data not changing *on its own* is not the
 * same as it not changing: a supervisor adds a machine, and every other browser
 * held the old list for the thirty minutes `useWorkCenters` was caching it. The
 * customer reported it as "clients say they don't see work centers".
 *
 * So these lists are pushed rather than polled. The server broadcasts
 * `.reference.changed` on the company's private channel when one of them moves
 * and the client invalidates — see `useRealtime`. The short `staleTime` in
 * `queries.ts` is the floor under that, for the deployments where Reverb is not
 * configured and the shifts where the queue worker is down.
 */
export function live(interval: number) {
  return {
    refetchInterval: interval,
    // Explicit rather than relying on the default: a phone in a pocket must not
    // be waking up to poll.
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  } as const
}
